/**
 * "WHAT THIS IS AND HOW IT WORKS" — the top of every module's Explain this (owner, 24 Oct 2026: "it doesnt
 * actually explain what it is, how it works. I feel like there should be a deeper dive explanation of what this
 * solution actually is").
 *
 * One small block for each piece of the plan: its plain name and what it is, always shown; how it works and what
 * is worth knowing behind one fold, open when the plan has a single piece. The words are the central library's
 * (features/advice/leverExplainers.ts), keyed by the same plan-piece kinds "What could go wrong" reads, so every
 * module explains a piece the same way. Doing nothing has no block, and nothing renders without a piece.
 */
import React from 'react';
import { leverExplainersFor } from '../../features/advice/leverExplainers.ts';
import type { RiskKind } from '../../strategy/strategyRisks.ts';

export function LeverExplainers({ kinds, className = '' }: { kinds: ReadonlyArray<RiskKind | string | null | undefined>; className?: string }) {
    const items = leverExplainersFor(kinds);
    if (items.length === 0) return null;
    const single = items.length === 1;
    return (
        <section className={className} data-testid="lever-explainers">
            <p className="mb-3 flex items-center gap-3 text-[12.5px] font-semibold text-brand-navy">
                <span className="shrink-0">What this is and how it works</span>
                <span className="h-px flex-1 bg-slate-200" aria-hidden />
            </p>
            <div className="space-y-3">
                {items.map(({ kind, explainer }) => (
                    <div key={kind} className="rounded-xl border border-slate-200 bg-white px-4 py-3" data-testid={`lever-explainer-${kind}`}>
                        <p className="text-[14px] font-semibold text-brand-navy">{explainer.title}</p>
                        <p className="mt-1 text-[13.5px] leading-relaxed text-slate-700">{explainer.what}</p>
                        <details className="group mt-2" open={single}>
                            <summary className="cursor-pointer list-none text-[13px] font-semibold text-blue-700 underline decoration-dotted underline-offset-2 hover:text-blue-900 [&::-webkit-details-marker]:hidden">
                                <span className="group-open:hidden">How it works, and what to know</span>
                                <span className="hidden group-open:inline">Hide how it works</span>
                            </summary>
                            <div className="mt-2 grid gap-3 sm:grid-cols-2">
                                <div>
                                    <p className="text-[12.5px] font-semibold text-slate-900">How it works</p>
                                    <ul className="mt-1 list-disc space-y-1 pl-5 text-[13px] leading-relaxed text-slate-700">
                                        {explainer.how.map((h) => <li key={h}>{h}</li>)}
                                    </ul>
                                </div>
                                <div>
                                    <p className="text-[12.5px] font-semibold text-slate-900">Worth knowing</p>
                                    <ul className="mt-1 list-disc space-y-1 pl-5 text-[13px] leading-relaxed text-slate-700">
                                        {explainer.worthKnowing.map((w) => <li key={w}>{w}</li>)}
                                    </ul>
                                </div>
                            </div>
                        </details>
                    </div>
                ))}
            </div>
        </section>
    );
}
